import { useGLTF } from "@react-three/drei";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { getFullAssetPath } from "shared/utils/assets";
import {
  defaultDoorHeight,
  defaultDoorWidth,
} from "shared/utils/doorSceneConstants";
import { GLTFMaterialResult } from "shared/utils/door";
import { GLTF } from "three-stdlib";
import { SilverHandlesGLTFResult } from "./handlesType";

type GLTFResult = GLTF & {
  nodes: {
    hinge_2: THREE.Mesh;
    hinge_1: THREE.Mesh;
    hinge_0: THREE.Mesh;
    hinge_2_mirrored: THREE.Mesh;
    hinge_1_mirrored: THREE.Mesh;
    hinge_0_mirrored: THREE.Mesh;
    frame_bottom: THREE.Mesh;
    kozijn_top: THREE.Mesh;
    kozijn_right: THREE.Mesh;
    kozijn_left: THREE.Mesh;
    handle_1: THREE.Mesh;
    handle_0: THREE.Mesh;
    glass: THREE.Mesh;
    frame_top: THREE.Mesh;
    frame_right: THREE.Mesh;
    frame_left: THREE.Mesh;
  };
  materials: {};
};

export function KadeurdeurDoor() {
  const { nodes } = useGLTF(
    getFullAssetPath("/objects-v3/kaderdeurEnkelGoodMirrored.glb"),
  ) as GLTFResult;

  const { nodes: nodesHandle, materials: materialsHandle } = useGLTF(
    getFullAssetPath("/objects-v3/kaderdeurSilverHandles-v1.glb"),
  ) as SilverHandlesGLTFResult;

  const { materials } = useGLTF(
    getFullAssetPath("/objects-v3/materialsCompressedv4.glb"),
  ) as GLTFMaterialResult;

  const {
    handle,
    getConfigurationHeight,
    getConfigurationWidth,
    getConfigurationColor,
    getConfigurationGlass,
    selfAssembleDoorData,
  } = useConfigurationOptionsContext();

  //Frame material
  const selectedMaterial =
    materials[getConfigurationColor() as keyof typeof materials];

  //Glass material
  const glassConfig = getConfigurationGlass();
  const selectedGlassMaterial =
    materials[glassConfig as keyof typeof materials];

  if (selectedGlassMaterial && !glassConfig.includes("Poedercoat")) {
    selectedGlassMaterial.envMapIntensity = 0.05;
  }

  const targetHeight = getConfigurationHeight(0);
  const heightScaleFactor = targetHeight / defaultDoorHeight;

  const targetWidth = getConfigurationWidth(0);
  const widthScaleFactor = targetWidth / defaultDoorWidth;

  // Door rotation - draairichting
  const isRotationRight =
    selfAssembleDoorData.measurements.rotation === "right";

  // Show handle lock based on handle type
  const showHandleLock =
    handle?.code.includes("cilinder") || handle?.code.includes("toilet");

  // Determine handle color based on handle type (zwart, rvsilver)
  const handleColor = handle?.code.includes("zwart")
    ? materials["PoedercoatGitZwart"]
    : materialsHandle.MergedBake_Baked;
  return (
    <group dispose={null}>
      <group rotation-y={isRotationRight ? Math.PI : 0}>
        {/* Hinges */}

        <mesh
          castShadow
          receiveShadow
          geometry={nodesHandle.hinge_2_Baked.geometry}
          material={handleColor}
          position={[0.46 * widthScaleFactor, 2.15 * heightScaleFactor, 0.02]}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodesHandle.hinge_1_Baked.geometry}
          material={handleColor}
          position={[0.46 * widthScaleFactor, 1.788 * heightScaleFactor, 0.02]}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodesHandle.hinge_0_Baked.geometry}
          material={handleColor}
          position={[0.46 * widthScaleFactor, 0.261 * heightScaleFactor, 0.02]}
        />

        {/* Hinges end */}

        {/* Mirrored hinges */}

        <mesh
          castShadow
          receiveShadow
          geometry={nodesHandle.hinge_2_mirrored_Baked.geometry}
          material={handleColor}
          position={[0.45 * widthScaleFactor, 2.15 * heightScaleFactor, -0.038]}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodesHandle.hinge_1_mirrored_Baked.geometry}
          material={handleColor}
          position={[0.45 * widthScaleFactor, 1.788 * heightScaleFactor, -0.04]}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodesHandle.hinge_0_mirrored_Baked.geometry}
          material={handleColor}
          position={[
            0.45 * widthScaleFactor,
            0.261 * heightScaleFactor,
            -0.038,
          ]}
        />

        {/* Mirrored hinges end */}
      </group>
      {/* Kozijns */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.kozijn_top.geometry}
        material={selectedMaterial}
        scale-x={widthScaleFactor}
        scale-y={heightScaleFactor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.kozijn_right.geometry}
        material={selectedMaterial}
        scale-x={widthScaleFactor}
        scale-y={heightScaleFactor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.kozijn_left.geometry}
        material={selectedMaterial}
        scale-x={widthScaleFactor}
        scale-y={heightScaleFactor}
      />
      {/* Kozijn end */}

      <group rotation-y={isRotationRight ? Math.PI : 0}>
        {/* Handles */}

        <mesh
          castShadow
          receiveShadow
          geometry={nodesHandle.handle_1_Baked.geometry}
          material={handleColor}
          visible={showHandleLock}
          position={[-0.421 * widthScaleFactor, 0.984, 0.001]}
        />

        <mesh
          castShadow
          receiveShadow
          geometry={nodesHandle.handle_0_Baked.geometry}
          material={handleColor}
          position={[-0.393 * widthScaleFactor, 1.073, 0]}
        />

        {/* Handles end */}
      </group>

      {/* Glass */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.glass.geometry}
        material={selectedGlassMaterial}
        scale-x={widthScaleFactor}
        scale-y={heightScaleFactor}
      />
      {/* Glass end */}

      {/* Frames */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.frame_top.geometry}
        material={selectedMaterial}
        scale-x={widthScaleFactor}
        scale-y={heightScaleFactor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.frame_right.geometry}
        material={selectedMaterial}
        scale-x={widthScaleFactor}
        scale-y={heightScaleFactor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.frame_left.geometry}
        material={selectedMaterial}
        scale-x={widthScaleFactor}
        scale-y={heightScaleFactor}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.frame_bottom.geometry}
        material={selectedMaterial}
        scale-x={widthScaleFactor}
        scale-y={heightScaleFactor}
      />
      {/* Frames end */}
    </group>
  );
}
